import React, { FormEventHandler, MouseEventHandler, useContext, useEffect, useState } from 'react';
import { ReactComponent as AddAnnotation } from 'assets/icons/add-annotation.svg';
import { ReactComponent as Annotation } from 'assets/icons/annotation.svg';
import { ReactComponent as AnnotationEngagement } from 'assets/icons/annotation-engagement.svg';
import dayjs from 'dayjs';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks'

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { createAnnotationAPI, removeAnnotationAPI, updateAnnotationAPI } from '../apis/annotations';
import { SessionContext } from '../auth';
import DashboardContext from '../pages/DashboardContext';
import { ICommonCampaignRow } from '../types/global';
import { IProfile } from '../types/users';
import { useFullYearFormat } from '../utils/format';

interface Props {
  row: ICommonCampaignRow;
  onShowEngagements: MouseEventHandler<HTMLElement>;
  engagementsVisible: boolean;
}

export default function Annotations(props: Props) {
  const { row, onShowEngagements, engagementsVisible } = props;
  const { profile } = useContext(SessionContext) as { profile: IProfile };
  const { updateRow } = useContext(DashboardContext);
  const canEdit = profile.organizations.some((x) => x.master);
  const dateFormat = useFullYearFormat();
  const popupState = usePopupState({ variant: 'popover', popupId: 'annotation' });
  const [text, setText] = useState('');
  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (!canEdit) return;
    const annotationDate = dayjs().toISOString();
    if (row.annotationId) {
      await updateAnnotationAPI(text, row);
      updateRow({ ...row, annotationText: text, annotationDate });
    } else {
      const { data: { id } } = await createAnnotationAPI(text, row);
      updateRow({ ...row, annotationText: text, annotationId: id, annotationDate });
    }
    popupState.close();
  };
  const onDelete = async () => {
    await removeAnnotationAPI(row);
    updateRow({ ...row, annotationText: undefined, annotationId: undefined, annotationDate: undefined });
    popupState.close();
  };
  useEffect(() => {
    if (popupState.isOpen) {
      setText(row.annotationText ?? '');
    }
  }, [popupState.isOpen]);
  const renderNonAdmin = () => (
    (row.hasEngagementAnnotationsOnly && !engagementsVisible) ? (
      <Tooltip title="There are annotations for the engagements. Click to view.">
        <IconButton
          sx={{
            padding: '4px',
            flexShrink: 0,
          }}
          onClick={onShowEngagements}
        >
          <Box component={AnnotationEngagement} />
        </IconButton>
      </Tooltip>
    ) : (
      <Box sx={{ width: '24px' }} />
    )
  );
  return (
    <>
      {row.annotationId && (
        <IconButton
          sx={{ padding: '4px', flexShrink: 0 }}
          {...bindTrigger(popupState)}
        >
          <Box component={Annotation} />
        </IconButton>
      )}
      {!row.annotationId && (canEdit ? (
        <Tooltip title="Add a note.">
          <IconButton
            sx={{
              padding: '4px',
              flexShrink: 0,
              color: '#FB8C00',
              transition: '.3s color',
              '&:hover': {
                color: '#8E8E8E',
              },
            }}
            {...bindTrigger(popupState)}
          >
            <Box component={AddAnnotation} />
          </IconButton>
        </Tooltip>
      ) : renderNonAdmin())}
      <Popover
        {...bindMenu(popupState)}
        PaperProps={{
          sx: {
            padding: '18px',
            background: '#8E8E8E',
          },
        }}
      >
        <form onSubmit={onSubmit}>
          {row.annotationId && (
            <Typography
              sx={{
                color: '#D6D6D6',
                paddingLeft: '12px',
                fontSize: 12,
                marginBottom: '6px',
              }}
            >
              {dayjs(row.annotationDate).format(`${dateFormat}, h:mm A`)}
            </Typography>
          )}
          <TextField
            sx={{ width: '364px' }}
            placeholder="Write an annotation text."
            value={text}
            onChange={({ target }) => setText(target.value)}
            inputProps={{ readOnly: !canEdit }}
          />
          {canEdit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <Button
                variant="text"
                size="small"
                sx={{ marginRight: '4px', color: '#F5F3F1' }}
                onClick={() => popupState.close()}
              >
                Cancel
              </Button>
              {row.annotationId && (
                <Button
                  variant="text"
                  size="small"
                  sx={{ marginRight: '4px', color: '#F5F3F1' }}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                type="submit"
                disabled={!text}
                sx={{
                  '&.Mui-disabled': {
                    color: '#e3f1ffcc',
                  },
                }}
              >
                Save
              </Button>
            </Box>
          )}
        </form>
      </Popover>
    </>
  )
}
